import { useEffect, useRef, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { useLocation } from "react-router";
import { ICONS } from "../constants/icons";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { ref, push, set, update } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { database, storage } from "../constants/firebase";
import MobileHeader from "../components/MobileHeader";
import { convertToWebP } from "../utils/utils";
import LoadingModal from "../components/LoadingModal";

const AddProduct = () => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const categories = ["E-Guides", "LUTs", "Presets", "E-Books", "SFX"];
    const navigate = useMoveTo();
    const location = useLocation();
    const { currentUser } = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currency, setCurrency] = useState("USD");
    const [form, setForm] = useState({
        title: "",
        price: "",
        category: "",
        coverImage: null,
        productFile: null,
        explainer: "",
        status: "Draft"
    });
    const coverInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const [isCoverImage, setIsCoverImage] = useState(false);
    const [isProductFile, setIsProductFile] = useState(false);

    // Loading Modal State
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Please wait while we save your product...");


    useEffect(() => {
        if (currentUser?.country === "South Africa") {
            setCurrency("ZAR");
        } else {
            setCurrency("USD");
        }
    }, [currentUser]);

    useEffect(() => {
        if (location.state?.product) {
            setForm({
                title: location.state.product.title,
                price: location.state.product.price || "",
                category: location.state.product.category,
                coverImage: null, // reset
                productFile: null,
                explainer: location.state.product.explainer || "",
                status: location.state.product.status || "Draft"
            });
            setIsCoverImage(!!location.state.product.coverImageUrl);
            setIsProductFile(!!location.state.product.fileUrl);
        }
    }, []);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };
    const handleCoverUpload = (e) => {
        setForm({ ...form, coverImage: e.target.files[0] });
    };
    const handleFileUpload = (e) => {
        setForm({ ...form, productFile: e.target.files[0] });
    };
    const triggerCoverUpload = () => {
        coverInputRef.current?.click();
    };
    const triggerFileUpload = () => {
        fileInputRef.current?.click();
    };

    const handleSave = async () => {
        if (!form.title || !form.price || !form.category) {
            window.showToast("Please fill all required fields", "error");
            return;
        }

        // Validate price
        const price = parseFloat(form.price);
        if (isNaN(price) || price <= 0) {
            window.showToast("Please enter a valid price greater than 0", "error");
            return;
        }

        // ✅ Require cover image only if creating a new product
        if (!location.state?.product?.id && !form.coverImage) {
            window.showToast("Please upload a cover image", "error");
            return;
        }

        // ✅ Require product file only if creating new
        if (!location.state?.product?.id && !form.productFile) {
            window.showToast("Please upload a product file", "error");
            return;
        }

        setIsSubmitting(true);
        setShowLoadingModal(true);
        setLoadingMessage("Saving product...");

        try {
            let coverImageUrl = null;
            let fileUrl = null;

            // Upload cover image
            if (form.coverImage) {
                setLoadingMessage("Converting cover image to WebP...");
                const webpFile = await convertToWebP(form.coverImage); // ✅ Convert first

                setLoadingMessage("Uploading cover image...");
                const storageReference = storageRef(
                    storage,
                    `products/covers/${Date.now()}_${webpFile.name}`
                );
                await uploadBytes(storageReference, webpFile);
                coverImageUrl = await getDownloadURL(storageReference);
            }

            // Upload product file
            if (form.productFile) {
                setLoadingMessage("Uploading product file...");
                const fileReference = storageRef(
                    storage,
                    `products/files/${Date.now()}_${form.productFile.name}`
                );
                await uploadBytes(fileReference, form.productFile);
                fileUrl = await getDownloadURL(fileReference);
            }

            setLoadingMessage("Saving product details...");

            if (location.state?.product?.id) {
                // ✅ EDIT product
                const updates = {
                    title: form.title,
                    price: parseFloat(form.price),
                    currency: currency,
                    category: form.category,
                    explainer: form.explainer,
                    status: form.status,
                    ...(coverImageUrl && { coverImageUrl }),
                    ...(fileUrl && { fileUrl }),
                };
                await update(ref(database, `Listings/${location.state.product.id}`), updates);
                window.showToast("Product updated successfully!", "success");
            } else {
                // ✅ ADD new product
                const newProductRef = push(ref(database, "Listings"));
                const productId = newProductRef.key;

                await set(newProductRef, {
                    id: productId,
                    type: "product", // 👈 important
                    title: form.title,
                    price: parseFloat(form.price),
                    currency: currency,
                    category: form.category,
                    explainer: form.explainer,
                    coverImageUrl: coverImageUrl || "",
                    fileUrl: fileUrl || "",
                    status: form.status,
                    userId: currentUser.uid,
                    createdAt: Date.now(),
                });

                setForm({
                    title: "",
                    price: "",
                    category: "",
                    coverImage: null,
                    productFile: null,
                    explainer: "",
                    status: "Draft"
                });
                window.showToast("Product saved successfully!", "success");
            }
        } catch (error) {
            console.error("Error saving product:", error);
            window.showToast("Failed to save product.", "error");
        } finally {
            setIsSubmitting(false);
            setShowLoadingModal(false);
        }
    };

    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            <MobileHeader title={location.state?.product ? "Edit Product" : "Add Product"} isBackBtn={true} />

            {/* Loading Modal */}
            <LoadingModal showLoadingModal={showLoadingModal} loadingMessage={loadingMessage} />

            <div className="flex">
                <Sidebar />
                <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">{location.state?.product ? "Edit Product" : "Add Product"}</h2>
                        <div />
                    </div>
                    <div className="max-w-md mx-auto md:pt-10 pt-3 space-y-6">
                        {/* Custom Category Dropdown */}
                        <div className="select-none">
                            <button
                                type="button"
                                className={`w-full flex items-center justify-between ${dropdownOpen ? 'rounded-t-xl font-semibold text-sm' : 'rounded-xl'} px-4 py-3 outline-none bg-[#F5F9FE] dark:bg-white text-[#7C8BA0] border border-gray-200 dark:border-0`}
                                onClick={() => setDropdownOpen((open) => !open)}
                            >
                                <span className={form.category ? "text-black dark:text-black" : "text-[#7C8BA0]"}>
                                    {form.category || "Category"}
                                </span>
                                <svg width="24" height="24" fill="none" stroke="#7C8BA0" strokeWidth="2" viewBox="0 0 24 24" className={dropdownOpen ? "rotate-180 transition" : "transition"}><path d="M6 9l6 6 6-6" /></svg>
                            </button>
                            <div
                                className={`overflow-hidden transition-all duration-700 ${dropdownOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} bg-[#F5F9FE] dark:bg-white rounded-b-xl border border-gray-200 dark:border-0`}
                                style={{ marginTop: 0 }}
                            >
                                {categories.map((cat, i) => (
                                    <button
                                        key={cat}
                                        type="button"
                                        className={`w-full text-left px-4 py-2 text-black dark:text-black font-semibold hover:bg-[#e3eaf3] dark:hover:bg-[#D9D9D9]/10] border-b border-gray-200 last:border-b-0 ${i === 0 ? '' : ''}`}
                                        onClick={() => {
                                            setForm({ ...form, category: cat });
                                            setDropdownOpen(false);
                                        }}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* Title */}
                        <input
                            type="text"
                            name="title"
                            placeholder="Title"
                            value={form.title}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-[#F5F9FE] text-black dark:text-black border border-gray-200 dark:border-0"
                        />
                        {/* Price */}
                        <input
                            type="number"
                            name="price"
                            placeholder={`Price (${currency})`}
                            value={form.price}
                            onChange={handleChange}
                            min="0"
                            step="0.01"
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-[#F5F9FE] text-black dark:text-black border border-gray-200 dark:border-0"
                        />
                        {/* Cover Image Upload */}
                        <div className="w-full cursor-pointer border-dashed border-2 border-gray-400 p-6 text-center rounded-xl bg-[#F5F9FE] dark:bg-white text-[#7C8BA0]" onClick={triggerCoverUpload}>
                            <input
                                ref={coverInputRef}
                                type="file"
                                accept="image/png,image/jpg,image/jpeg,image/webp"
                                className="hidden"
                                onChange={handleCoverUpload}
                            />
                            <img src={ICONS.upload} className="mx-auto mb-2 w-8 h-8" alt="Upload" />
                            <p className="font-semibold text-base text-[#7C8BA0]">Upload the Product cover image:</p>
                            <p className="text-xs text-[#7C8BA0]">Use 16:9 for best results; other images will be auto-cropped (.png, .jpg, .jpeg, .webp).</p>
                            {form.coverImage && <p className="mt-2 text-xs text-green-600">Selected: {form.coverImage.name}</p>}
                            {!form.coverImage && isCoverImage && (
                                <p className="mt-2 text-xs text-green-600">
                                    Cover image already exists
                                </p>
                            )}
                        </div>
                        {/* Product File Upload */}
                        <div className="w-full cursor-pointer border-dashed border-2 border-gray-400 p-6 text-center rounded-xl bg-[#F5F9FE] dark:bg-[#F5F9FE] text-[#7C8BA0]" onClick={triggerFileUpload}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="
                                .zip,
    .cube,.3dl,.lut,.aml,.look,.prfpset,.ffx,.eprj,.drx,.motn,.xmp,
    .epub,.pdf,.mobi,.azw3,.iba,.rtf,.txt,.html,.htm,
    .wav,.aiff,.mp3,.aac,.flac,.ogg,.pdf"
                                className="hidden"
                                onChange={handleFileUpload}
                            />
                            <img src={ICONS.upload} className="mx-auto mb-2 w-8 h-8" alt="Upload" />
                            <p className="font-semibold text-base text-[#7C8BA0]">Product File:</p>
                            <p className="text-xs text-[#7C8BA0]">
                                Supported formats:<br />
                                <strong>Audio:</strong> WAV, AIFF, MP3, AAC, FLAC, OGG<br />
                                <strong>Documents & E-Books:</strong> ZIP, EPUB, PDF, MOBI, AZW3, IBA, RTF, TXT, HTML, HTM<br />
                                <strong>Specialty:</strong> CUBE, 3DL, LUT, AML, LOOK, PRFPSET, FFX, EPRJ, DRX, MOTN, XMP
                            </p>

                            {form.productFile && <p className="mt-2 text-xs text-green-600">Selected: {form.productFile.name}</p>}
                            {!form.productFile && isProductFile && (
                                <p className="mt-2 text-xs text-green-600">
                                    Product file already exists
                                </p>
                            )}
                        </div>
                        {/* Explainer Text */}
                        <input
                            type="text"
                            name="explainer"
                            placeholder="Explainer Text"
                            value={form.explainer}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-[#F5F9FE] text-black dark:text-black border border-gray-200 dark:border-0"
                        />
                        {/* Status */}
                        <select
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWNoZXZyb24tZG93biI+PHBhdGggZD0ibTYgOSA2IDYgNi02Ii8+PC9zdmc+')] bg-no-repeat bg-[position:right_0.5rem_center] bg-[size:1.5em_1.5em]"
                        >
                            <option value="Draft">Draft</option>
                            <option value="Published">Published</option>
                        </select>
                        {/* Save Button */}
                        <button
                            onClick={handleSave}
                            className="bg-[#327691] text-white text-lg font-semibold py-3 rounded-xl w-full mt-4"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Saving..." : "Save Product"}
                        </button>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AddProduct;